import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {GROWL_TYPE, hideMessageGrowl} from '@Components/message-growl';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './snackbar-message-growl.module.scss';

interface SnackbarMessageGrowlProps {
  className?: string;
  translateXValue?: number;
}

export function SnackbarMessageGrowl({className = '', ...props}: SnackbarMessageGrowlProps): ReactElement<any> | null {
  const messageGrowl = useAppSelector((state) => {
    return state.messageGrowl;
  });
  const currentLoading = useAppSelector((state) => {
    return state.loadingToast.currentLoading;
  });
  const [messageGrowlDummyState, setMessageGrowlDummyState] = useState(false);
  const [closeEnded, setCloseEnded] = useState(false);
  const dispatch = useAppDispatch();

  useEffect((): void => {
    if (!messageGrowlDummyState) {
      setTimeout((): void => {
        setCloseEnded(true);
      }, 500);
    } else {
      setTimeout(() => {
        setMessageGrowlDummyState(false);
        dispatch(hideMessageGrowl(messageGrowl.key));
      }, messageGrowl.interval);
    }
  }, [messageGrowlDummyState, messageGrowl.key]);

  useEffect((): void => {
    if (messageGrowl.show) {
      setCloseEnded(false);
      if (!currentLoading.show) {
        setTimeout(() => {
          setMessageGrowlDummyState(true);
        }, 500);
      }
    }
  }, [messageGrowl.show, currentLoading.show, messageGrowl.key]);

  const getMessageGrowlClasses = (): string => {
    switch (messageGrowl.type) {
      case GROWL_TYPE.DANGER:
        return styles.danger;
      case GROWL_TYPE.PREMIUM:
        return styles.premium;
      case GROWL_TYPE.SECONDARY:
        return styles.secondary;
      case GROWL_TYPE.NEUTRAL:
        return styles.neutral;
      case GROWL_TYPE.SUCCESS:
      default:
        return styles.success;
    }
  };

  const isShowing = (): boolean => {
    return messageGrowl.show && messageGrowlDummyState;
  };

  const getIcon = (): ReactElement<any> | null => {
    if (messageGrowl.type === GROWL_TYPE.DANGER) {
      return <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="content-body-white" />;
    }

    if (messageGrowl.icon) {
      return <Icon icon={messageGrowl.icon} size={IconSize.SIZE_ICON_16} className="content-body-white" />;
    }

    return null;
  };

  const isIconShowing = (): boolean => {
    return messageGrowl.type === GROWL_TYPE.DANGER || !!messageGrowl.icon;
  };

  return (
    <div
      style={{
        transform: props.translateXValue !== undefined ? `translateX(calc(-50% + ${props.translateXValue}px))` : 'translateX(-50%)',
      }}
      className={`${styles.snackbar} ${className} flexbox flex-items-center ${getMessageGrowlClasses()} ${isShowing() ? styles.in : styles.out} flex-h-row radius-round ${
        !isShowing() && closeEnded ? styles.outEnded : ''
      } `}
    >
      {getIcon()}
      <Text
        dangerouslySetInnerHTML={messageGrowl.dangerouslySetInnerHTML}
        val={messageGrowl.text}
        className={`content-body-white ${isIconShowing() ? styles.marginLeft : ''}`}
        size={TextSize.XXSMALL}
      />
      {messageGrowl.showCloseIcon ? (
        <Icon
          icon="icon-close"
          className="content-body-white spacing-m-l-4"
          size={IconSize.SIZE_ICON_16}
          onClick={(): void => {
            dispatch(hideMessageGrowl(messageGrowl.key));
          }}
        />
      ) : null}
    </div>
  );
}
